import { CellPreviewCard } from '@renderer/overlay/Sidepanel/components/cell-preview-card'
import { BriefLinkIcon } from './link-pill'
import { BriefLinkPreviewCard } from './brief-link-preview'
import { normalizeBriefLinkUrl } from '../link-previews'
import type { BriefLink, BriefLinkPreview } from '../types'

interface BriefCitationsProps {
  links: Array<BriefLink>
  /**
   * The bullet's own support line. It replaces the preview's merged support,
   * which spans every bullet citing the record. Under one bullet, the other
   * bullets' reasoning is the wrong answer to "why is this here?".
   */
  support: string
  previews?: Record<string, BriefLinkPreview>
}

/**
 * The records a bullet was written from, each hover-previewing its source.
 * Inline at the end of the line rather than a footnote row, because the
 * citation belongs to the claim it supports.
 *
 * Flat siblings of the bullet text, with no wrapper: the hover card's trigger
 * is a `div`, which a wrapping `span` could not legally contain.
 */
export function BriefCitations({ links, support, previews }: BriefCitationsProps) {
  if (links.length === 0) return null

  return (
    <>
      {links.map((link) => {
        const preview = previews?.[normalizeBriefLinkUrl(link.url)]
        return (
          <BriefCitation
            key={link.url}
            link={link}
            preview={preview && { ...preview, support: support.trim() ? [support] : [] }}
          />
        )
      })}
    </>
  )
}

// first:ml-1.5 opens a word's worth of space after the sentence; the rest sit
// tight as one cluster. align-middle centers the chip on the x-height midpoint,
// which reads low against a 14px line, so it also lifts a pixel.
const CITATION_CLASS = 'ml-1 inline-flex w-auto -translate-y-px align-middle first:ml-1.5'

function BriefCitation({ link, preview }: { link: BriefLink; preview?: BriefLinkPreview }) {
  const anchor = (
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer"
      aria-label={`Open ${link.title}`}
      className="border-border hover:border-hover hover:bg-element-hover corner-smooth flex items-center justify-center rounded-md border p-[3px] transition-colors"
    >
      <BriefLinkIcon url={link.url} source={link.source} size={12} />
    </a>
  )

  if (!preview) {
    return <span className={CITATION_CLASS}>{anchor}</span>
  }

  return (
    <CellPreviewCard
      triggerClassName={CITATION_CLASS}
      popupClassName="w-[320px] max-w-[320px]"
      density="tight"
      preview={<BriefLinkPreviewCard link={link} preview={preview} />}
    >
      {anchor}
    </CellPreviewCard>
  )
}
